// Google Analytics 4 loader that respects DSGVO/GDPR consent.
//
// - Nothing loads until the user grants the "statistics" category.
// - Uses `anonymize_ip: true` and Consent Mode v2 defaults (all denied). We
//   only flip analytics_storage=granted after explicit opt-in.
// - Provides a `track(event, params)` wrapper that becomes a no-op when
//   consent is not granted.

import { GA_MEASUREMENT_ID } from "./legal";

const SCRIPT_ID = "ga-runtime-loader";
const INIT_ID = "ga-runtime-init";

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

// Consent Mode v2 defaults — call once as early as possible.
export function initConsentMode() {
  ensureDataLayer();
  if (typeof window === "undefined" || !window.gtag) return;
  if (window.__gaConsentInit) return;
  window.__gaConsentInit = true;
  window.gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  window.gtag("js", new Date());
}

export function loadAnalytics() {
  if (typeof document === "undefined") return;
  ensureDataLayer();

  // Update consent to granted for analytics
  window.gtag("consent", "update", { analytics_storage: "granted" });

  if (document.getElementById(SCRIPT_ID)) return; // already loaded

  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  const init = document.createElement("script");
  init.id = INIT_ID;
  init.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('config', '${GA_MEASUREMENT_ID}', {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });
  `;
  document.head.appendChild(init);
}

export function unloadAnalytics() {
  if (typeof document === "undefined") return;
  // Withdraw consent
  ensureDataLayer();
  window.gtag("consent", "update", { analytics_storage: "denied" });

  for (const id of [SCRIPT_ID, INIT_ID]) {
    const el = document.getElementById(id);
    if (el) el.remove();
  }

  // Clean up any GA cookies from this domain (best-effort)
  try {
    const host = window.location.hostname;
    const base = host.startsWith("www.") ? host.slice(4) : host;
    const gaCookies = ["_ga", "_ga_" + GA_MEASUREMENT_ID.replace("G-", ""), "_gid", "_gat"];
    const paths = ["/", ""];
    const domains = [host, "." + host, base, "." + base];
    for (const c of gaCookies) {
      for (const p of paths) {
        for (const d of domains) {
          document.cookie = `${c}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}; domain=${d}`;
          document.cookie = `${c}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}`;
        }
      }
    }
  } catch {
    /* noop */
  }
}

/**
 * Fire a custom event. Silently no-ops when analytics is not loaded.
 * @param {string} event - GA event name (e.g. 'login', 'add_trip')
 * @param {object} params - additional parameters
 */
export function track(event, params = {}) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  if (!document.getElementById(SCRIPT_ID)) return; // not consented
  try {
    window.gtag("event", event, params);
  } catch {
    /* noop */
  }
}
